import { useState } from "react";

import { formatMcc } from "@cpos/shared";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/base/sheet";

import {
  formatEpoch,
  formatIso,
  type BusinessProfile,
  type MerchantLifecycle,
  type ParsedStripeErrors,
  type UserProfile,
} from "../types";
import { personRecordOf } from "@/application/merchants/personal_info_edit";
import { formatCountry } from "./country";
import { OverlayRow, OverlaySection } from "./OverlaySection";
import { PeopleList } from "./PeopleList";
import {
  formatAddress,
  formatDob,
  parseCompany,
  parsePerson,
  type PersonFields,
} from "./person";
import { AuthSheet } from "./AuthSheet";
import { FlagsSheet } from "./FlagsSheet";
import { ReferralCard } from "./ReferralCard";
import { StageBadge } from "./StageBadge";
import { StripeIntegrationCard } from "./StripeIntegrationCard";

interface ProfileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  business: BusinessProfile;
  users: UserProfile[] | null;
  lifecycle: MerchantLifecycle | null;
  stripeErrors: ParsedStripeErrors | null;
  onRefresh: () => void;
  onEditBusinessInfo: () => void;
  onEditPersonalInfo: () => void;
}

interface BankAccount {
  lastUpdatedAt?: number | null;
}

/**
 * Everything known about a merchant, grouped by the question being asked:
 * who the account belongs to, how to reach them, what Stripe thinks, what
 * the business is, who else is on it, and what the platform recorded.
 */
export function ProfileSheet({
  open,
  onOpenChange,
  business,
  users,
  lifecycle,
  stripeErrors,
  onRefresh,
  onEditBusinessInfo,
  onEditPersonalInfo,
}: ProfileSheetProps) {
  const isIndividual = business.businessType === "individual";
  const person = parsePerson<PersonFields>(
    isIndividual ? business.individual : business.representative,
  );
  const company = parseCompany(business.company);
  const bankAccount = parsePerson<BankAccount>(business.bankAccount);

  /**
   * Oldest profile is the account owner. `business.userIds[0]` holds an owner
   * string rather than a `UserProfile.userId`, and the index returns no
   * particular order, so neither is a reliable way to pick.
   */
  const primaryUser = users
    ? ([...users].sort((a, b) =>
        (a.createdAt ?? "") < (b.createdAt ?? "") ? -1 : 1,
      )[0] ?? null)
    : null;

  const [authOpen, setAuthOpen] = useState(false);
  const [flagsOpen, setFlagsOpen] = useState(false);

  const dob = person
    ? formatDob(person.dobDay, person.dobMonth, person.dobYear)
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-[563px]">
        <SheetHeader className="border-border border-b px-6 py-4">
          <SheetTitle className="text-foreground text-lg font-semibold">
            Profile
          </SheetTitle>
        </SheetHeader>

        <div className="overflow-y-auto">
          <OverlaySection title="Account information">
            <OverlayRow label="ID">
              <span className="font-mono text-xs break-all">{business.id}</span>
            </OverlayRow>
            <OverlayRow label="Email">
              {/* The owner's profile email, which mirrors their Cognito login. */}
              {primaryUser?.email ?? null}
            </OverlayRow>
            <OverlayRow label="Terms accepted">
              {business.tosAcceptedAt
                ? formatEpoch(business.tosAcceptedAt)
                : null}
            </OverlayRow>
            <OverlayRow label="Terms accepted IP">
              {business.tosAcceptedIp ?? null}
            </OverlayRow>
          </OverlaySection>

          <OverlaySection
            title="Contact information"
            action={
              personRecordOf(business) !== null ? (
                <button
                  type="button"
                  onClick={onEditPersonalInfo}
                  aria-label="Edit personal info"
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Edit
                </button>
              ) : undefined
            }
          >
            {person ? (
              <>
                <OverlayRow label="Title">
                  {person.relationshipTitle ?? null}
                </OverlayRow>
                <OverlayRow label="Name">
                  {`${person.firstName ?? ""} ${person.lastName ?? ""}`.trim()}
                </OverlayRow>
                <OverlayRow label="Email">{person.email ?? null}</OverlayRow>
                <OverlayRow label="Phone number">
                  {person.phone ?? null}
                </OverlayRow>
                <OverlayRow label="Date of birth">{dob}</OverlayRow>
                <OverlayRow label="Address">{formatAddress(person)}</OverlayRow>
                <OverlayRow label="SSN last 4">
                  {person.ssnLast4 ?? null}
                </OverlayRow>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">
                No data available.
              </p>
            )}
          </OverlaySection>

          <OverlaySection title="Stripe integration">
            <StripeIntegrationCard
              business={business}
              stripeErrors={stripeErrors}
            />
          </OverlaySection>

          <OverlaySection title="Account verification">
            <OverlayRow label="Onboarding status">
              {business.onboardingStatus ? (
                <StageBadge stage={business.onboardingStatus} />
              ) : null}
            </OverlayRow>
            <OverlayRow label="Bank account updated">
              {bankAccount?.lastUpdatedAt
                ? formatEpoch(bankAccount.lastUpdatedAt)
                : null}
            </OverlayRow>
          </OverlaySection>

          <OverlaySection
            title="Business details"
            action={
              <button
                type="button"
                onClick={onEditBusinessInfo}
                aria-label="Edit business info"
                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
              >
                Edit
              </button>
            }
          >
            <OverlayRow label="Name">
              {business.businessName ?? null}
            </OverlayRow>
            <OverlayRow label="Statement descriptor">
              {business.businessStatementDescriptor ?? null}
            </OverlayRow>
            <OverlayRow label="Country">
              {formatCountry(business.businessCountry) || null}
            </OverlayRow>
            <OverlayRow label="Business type">
              {business.businessType?.replace(/_/g, " ") ?? null}
            </OverlayRow>
            <OverlayRow label="Merchant category (MCC)">
              {business.merchantCategoryCode
                ? formatMcc(business.merchantCategoryCode)
                : null}
            </OverlayRow>
            <OverlayRow label="Support phone">
              {business.businessSupportPhone ?? null}
            </OverlayRow>
            <OverlayRow label="Website">
              {business.businessUrl ? (
                <a
                  href={
                    /^https?:\/\//i.test(business.businessUrl)
                      ? business.businessUrl
                      : `https://${business.businessUrl}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-blue-600 hover:text-blue-800"
                >
                  {business.businessUrl}
                </a>
              ) : null}
            </OverlayRow>
            <OverlayRow label="Slug">
              {business.businessSlug ?? null}
            </OverlayRow>
            <OverlayRow label="Microsite enabled">
              {business.micrositeEnabled ? "Yes" : "No"}
            </OverlayRow>

            {/* The business address lives in `company` for every business type. */}
            {company && (
              <>
                <OverlayRow label="Business address">
                  {formatAddress(company)}
                </OverlayRow>
                <OverlayRow label="Business phone">
                  {company.phone ?? null}
                </OverlayRow>
                <OverlayRow label="Tax ID">
                  {company.taxId ? (
                    <span className="font-mono text-xs break-all">
                      {company.taxId}
                    </span>
                  ) : null}
                </OverlayRow>
                <OverlayRow label="Registration number">
                  {company.registrationNumber ? (
                    <span className="font-mono text-xs break-all">
                      {company.registrationNumber}
                    </span>
                  ) : null}
                </OverlayRow>
              </>
            )}
          </OverlaySection>

          <OverlaySection title="People">
            <PeopleList
              business={business}
              users={users}
              onRefresh={onRefresh}
            />
          </OverlaySection>

          <OverlaySection title="Platform information">
            <OverlayRow label="Created">
              {business.createdAt != null
                ? formatIso(business.createdAt)
                : null}
            </OverlayRow>
            <OverlayRow label="Auth">
              <ViewLink onClick={() => setAuthOpen(true)} label="auth state" />
            </OverlayRow>
            <OverlayRow label="Flags">
              <ViewLink
                onClick={() => setFlagsOpen(true)}
                label="flag resolution"
              />
            </OverlayRow>
            <OverlayRow label="Referral">
              <ReferralCard
                businessId={business.id}
                primaryUser={primaryUser}
                lifecycle={lifecycle}
                onRefresh={onRefresh}
              />
            </OverlayRow>
          </OverlaySection>
        </div>
      </SheetContent>

      <AuthSheet
        open={authOpen}
        onOpenChange={setAuthOpen}
        businessId={business.id}
      />

      <FlagsSheet
        open={flagsOpen}
        onOpenChange={setFlagsOpen}
        businessId={business.id}
      />
    </Sheet>
  );
}

/** Opens a panel over this one. Named for what it opens, for screen readers. */
function ViewLink({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`View ${label}`}
      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
    >
      View
    </button>
  );
}
